import { FC } from "react";
import PieCharts from "../PieCharts";
import Badge from "../Badge";
import Card from "../Card";
import { GeneralProgressData, ShredsProgressData } from "@/@types/home";

interface GeneralVueProps {
  generalProgressData: GeneralProgressData[] | undefined
  shredsProgressData: ShredsProgressData[] | undefined
}

const GeneralVue: FC<GeneralVueProps> = ({ generalProgressData, shredsProgressData }) => {
  const hasGeneralData = generalProgressData && generalProgressData.length > 0;
  const hasShredsData = shredsProgressData && shredsProgressData.length > 0;
  
  const EmptyState = ({ message }: { message: string }) => (
    <div className="flex flex-col items-center justify-center h-full min-h-[240px] text-center">
      <svg
        className="w-16 h-16 text-gray-300 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <p className="text-gray-500 text-sm">{message}</p>
    </div>
  );
  
  return (
    <Card borderColor="[#dbe0f6]" bgColor="white" minHeight="[24rem]">
      <div className="px-1 xl:px-5 grid grid-cols-12 min-h-[378px]">
        {/* General Progress Section */}
        <div className="xl:col-span-6 md:col-span-4 col-span-12 md:border-r-[1px] border-b-[1px] md:border-b-0 border-r-[#DBE0F6] py-5 flex flex-col justify-stretch">
          <div className="flex flex-col justify-start items-start mb-4">
            <h1 className="text-xl font-bold w-full max-w-[180px]">
              General Progress
            </h1>
            <p className="text-sm text-[#8F95B2] font-semibold">
              General Projects Progress
            </p>
          </div>
          {hasGeneralData ? (
            <div className="grid grid-cols-12 pr-1 h-full min-h-[240px]">
              <div className="xl:col-span-6 col-span-12">
                <PieCharts
                  data={generalProgressData}
                  innerRadius={70}
                  outerRadius={90}
                  fixedActive
                  custom
                />
              </div>
              <div className="xl:col-span-6 col-span-12 flex flex-col justify-center xl:items-center items-start mb-3">
                {generalProgressData.map((item, index) => (
                  <div key={index} className="mb-3">
                    <div className="flex items-center text-sm font-semibold gap-3">
                      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span>{item.label}</span>
                    </div>
                    <div className="ml-6 text-sm text-[#8F95B2]">
                      Count: {item.count}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <EmptyState message="No general progress data available" />
          )}
        </div>
        
        {/* Shreds Progress Section */}
        <div className="xl:col-span-6 md:col-span-8 col-span-12 py-5 xl:pl-5 pl-1 flex flex-col justify-stretch">
          <div className="flex flex-col justify-start items-start mb-4">
            <h1 className="text-xl font-bold w-full max-w-[180px]">
              Shreds progress
            </h1>
            <p className="text-sm text-[#8F95B2] font-semibold">
              All shreds progress statistics
            </p>
          </div>
          {hasShredsData ? (
            <div className="grid grid-cols-12 xl:gap-5 gap-2 h-full">
              {shredsProgressData.map((item, index) => (
                <div className="col-span-6 grid grid-cols-12" key={index}>
                  <div className="col-span-5">
                    <PieCharts
                      data={[
                        { name: item.name, value: item.value, color: item.color },
                        {
                          name: item.name,
                          value: 100 - item.value,
                          color: item.accentColor,
                        },
                      ]}
                      innerRadius={30}
                      outerRadius={40}
                      fixedActive
                    />
                  </div>
                  <div className="col-span-7 flex flex-col justify-center">
                    <div className="ml-0">
                      <h6 className="text-base text-[#8F95B2] font-bold mb-1">
                        {item.label}
                      </h6>
                      <span className="text-sm text-[#8F95B2] pr-1">
                        Count: {item.count}
                      </span>
                      <Badge classColor={item.classColor} size="sm">
                        {item.name}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState message="No shreds progress data available" />
          )}
        </div>
      </div>
    </Card>
  );
};

export default GeneralVue;